/**
 * As cores da marca do tenant, aplicadas ao widget.
 *
 * Elas vem do MESMO lugar que a vitrine usa (`service_ecommerce_globals`:
 * `primary_color` e `secondary_color`), e nao de uma paleta so do chat: duas
 * fontes de cor para o mesmo cliente sempre divergem, e quem repara e o
 * visitante, que ve um chat de outra cor colado no site.
 *
 * Duas coisas que este arquivo NAO faz de bobeira:
 *
 * 1. **Nao escreve em `--accent` direto.** Estilo inline no `:root` vence
 *    qualquer regra da folha, inclusive as de tema — carimbar `--accent` ali
 *    mataria a variante escura. Ele escreve em `--marca-*`, e a folha decide
 *    qual usar em cada tema.
 * 2. **Nao confia que a cor da marca serve nos dois temas.** Uma marca verde
 *    escura sobre o fundo escuro do tema noturno some. A versao escura e
 *    clareada aqui, com a conta feita no RGB.
 */

export type BrandColors = {
  accent?: string | null
  accentSecondary?: string | null
}

type RGB = { r: number; g: number; b: number }

/** `#abc`, `#aabbcc` (com ou sem alfa) -> RGB. Qualquer outra coisa: `null`. */
export function parseHex(cor: string): RGB | null {
  const limpo = cor.trim().replace(/^#/, "")
  const hex =
    limpo.length === 3 || limpo.length === 4
      ? limpo
          .slice(0, 3)
          .split("")
          .map((c) => c + c)
          .join("")
      : limpo.length === 6 || limpo.length === 8
        ? limpo.slice(0, 6)
        : null
  if (!hex || !/^[0-9a-f]{6}$/i.test(hex)) return null
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  }
}

const paraHex = ({ r, g, b }: RGB): string =>
  `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("")}`

/**
 * Luminancia relativa (WCAG). Serve para duas decisoes: se o texto por cima
 * deve ser claro ou escuro, e se a cor precisa ser clareada no tema noturno.
 */
export function luminancia(cor: RGB): number {
  const canal = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * canal(cor.r) + 0.7152 * canal(cor.g) + 0.0722 * canal(cor.b)
}

/** Preto ou branco por cima da cor — o que tiver mais contraste. */
export function tintaSobre(cor: RGB): string {
  return luminancia(cor) > 0.45 ? "#14181a" : "#ffffff"
}

const misturar = (cor: RGB, alvo: RGB, peso: number): RGB => ({
  r: cor.r + (alvo.r - cor.r) * peso,
  g: cor.g + (alvo.g - cor.g) * peso,
  b: cor.b + (alvo.b - cor.b) * peso,
})

const BRANCO: RGB = { r: 255, g: 255, b: 255 }

/**
 * A mesma cor, utilizavel no tema escuro.
 *
 * So clareia o que esta escuro demais para se destacar do fundo noturno; uma
 * marca que ja e clara volta igual, sem lavar a identidade dela.
 */
export function paraTemaEscuro(cor: RGB): RGB {
  const l = luminancia(cor)
  if (l >= 0.22) return cor
  // Quanto mais escura, mais clareia — teto em 45% de branco para nao virar pastel.
  const peso = Math.min(0.45, (0.22 - l) * 1.8)
  return misturar(cor, BRANCO, peso)
}

/** As variaveis que a folha de estilo consome. `null` = apaga a variavel. */
export function brandVars(brand: BrandColors): Record<string, string | null> {
  const vars: Record<string, string | null> = {
    "--marca": null,
    "--marca-escura": null,
    "--marca-tinta": null,
    "--marca-tinta-escura": null,
    "--marca-2": null,
    "--marca-2-escura": null,
    "--marca-2-tinta": null,
    "--marca-2-tinta-escura": null,
  }

  const aplicar = (cor: string | null | undefined, prefixo: "--marca" | "--marca-2") => {
    if (!cor) return
    const rgb = parseHex(cor)
    if (!rgb) {
      // `rgb()`, `hsl()` ou nome de cor: usa como veio nos dois temas. Sem os
      // canais nao da para calcular contraste nem clarear, e chutar seria pior
      // do que nao mexer.
      vars[prefixo] = cor
      vars[`${prefixo}-escura`] = cor
      return
    }
    const escura = paraTemaEscuro(rgb)
    vars[prefixo] = paraHex(rgb)
    vars[`${prefixo}-escura`] = paraHex(escura)
    vars[`${prefixo}-tinta`] = tintaSobre(rgb)
    vars[`${prefixo}-tinta-escura`] = tintaSobre(escura)
  }

  aplicar(brand.accent, "--marca")
  aplicar(brand.accentSecondary, "--marca-2")
  return vars
}

/** Escreve as variaveis no `:root` do documento do widget. */
export function applyBrandColors(root: HTMLElement, brand: BrandColors): void {
  for (const [nome, valor] of Object.entries(brandVars(brand))) {
    if (valor === null) root.style.removeProperty(nome)
    else root.style.setProperty(nome, valor)
  }
}
